import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError, BACKEND } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import FileUploader from "../../components/admin/FileUploader";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Quotes } from "@phosphor-icons/react";
import { SortableProvider, SortableItems, SortableItem, DragHandle } from "../../components/admin/SortableList";

const empty = { name: "", role: "", company: "", photo_url: "", quote: "", rating: 5, is_active: true };

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/admin/testimonials");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, rating: parseInt(form.rating) || 0 };
      if (editing) await api.put(`/admin/testimonials/${editing.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Testimonials"
        subtitle="Partner and customer quotes shown on the public home page."
        actions={
          <Button onClick={openNew} data-testid="add-testimonial-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New testimonial
          </Button>
        }
      />
      <div className="p-8">
        <SortableProvider items={items} setItems={setItems} reorderEndpoint="/admin/reorder/testimonials" layout="grid">
          <SortableItems items={items} strategy="grid">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="testimonials-grid">
              {items.length === 0 && <div className="col-span-full text-center py-12 text-[#4B5563]">No testimonials yet.</div>}
              {items.map((it) => (
                <SortableItem key={it.id} id={it.id} as="div" className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={`testimonial-card-${it.id}`}>
                  {(handleProps) => (
                    <>
                      <div className="flex items-start gap-3">
                        <DragHandle handleProps={handleProps} />
                        <Quotes size={20} weight="duotone" className="text-[#C2410C] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#1F2937] truncate">{it.name}</div>
                          <div className="text-xs text-[#4B5563] truncate">{[it.role, it.company].filter(Boolean).join(" · ") || "—"}</div>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-[#4B5563] line-clamp-3">{it.quote}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"}`}>
                          {it.is_active ? "Visible" : "Hidden"} · {it.rating || 0}★
                        </span>
                        <div>
                          <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                          <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                        </div>
                      </div>
                    </>
                  )}
                </SortableItem>
              ))}
            </div>
          </SortableItems>
        </SortableProvider>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="t-name" /></div>
              <div className="space-y-2"><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Chief Pharmacist" data-testid="t-role" /></div>
              <div className="space-y-2 md:col-span-2"><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Apollo Hospitals" data-testid="t-company" /></div>
            </div>
            <div className="space-y-2">
              <Label>Quote *</Label>
              <Textarea rows={5} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="What the partner / customer says about you." data-testid="t-quote" />
            </div>
            <div className="space-y-2">
              <Label>Rating</Label>
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: parseInt(v) })}>
                <SelectTrigger data-testid="t-rating"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[5, 4, 3, 2, 1, 0].map((n) => <SelectItem key={n} value={String(n)}>{n} {n ? "★".repeat(n) : "— no stars"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FileUploader kind="image" accept="image/*" label="Avatar / photo (optional)" currentUrl={form.photo_url} onUploaded={(url) => setForm({ ...form, photo_url: url })} testId="upload-photo" />
            <p className="text-xs text-[#4B5563]">If no photo, the public site shows a gradient circle with the person's initials.</p>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="t-active" />
              <Label>Visible on site</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-testimonial" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
